import type { SyncProvider, SyncItem, SyncResult, SyncStatus, SyncedItem } from "../types.js";

const DEFAULT_STATUS_MAP: Record<string, SyncStatus> = {
  "To Do": "todo",
  Backlog: "backlog",
  "In Progress": "in_progress",
  "In Review": "review",
  Done: "done",
  Cancelled: "cancelled",
  Closed: "done",
};

const REVERSE_STATUS_MAP: Record<SyncStatus, string> = {
  backlog: "Backlog",
  todo: "To Do",
  in_progress: "In Progress",
  review: "In Review",
  done: "Done",
  cancelled: "Cancelled",
};

function extractJiraConfig(projectKey: string): { host: string; project: string } {
  // projectKey format: "mycompany.atlassian.net/PROJECT" or just "PROJECT"
  // If it contains a dot, treat the first part as the host
  if (projectKey.includes("/")) {
    const [host, project] = projectKey.split("/", 2);
    return { host: `https://${host}`, project };
  }
  // Fall back to JIRA_HOST env var
  const host = process.env.JIRA_HOST;
  if (!host) {
    throw new Error(
      "Jira host not found. Use format 'mycompany.atlassian.net/PROJECT' or set JIRA_HOST env var.",
    );
  }
  return { host: host.startsWith("https://") ? host : `https://${host}`, project: projectKey };
}

async function jiraRequest(
  host: string,
  apiKey: string,
  method: string,
  path: string,
  body?: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const email = process.env.JIRA_EMAIL ?? "";

  const res = await fetch(`${host}/rest/api/3${path}`, {
    method,
    headers: {
      Authorization: `Basic ${Buffer.from(`${email}:${apiKey}`).toString("base64")}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Jira API error (${res.status}): ${text}`);
  }

  // Some Jira endpoints return 204 No Content
  if (res.status === 204) return {};

  return res.json() as Promise<Record<string, unknown>>;
}

function buildAdfDescription(item: SyncItem): Record<string, unknown> {
  return {
    version: 1,
    type: "doc",
    content: [
      {
        type: "paragraph",
        content: [{ type: "text", text: item.description }],
      },
      { type: "rule" },
      {
        type: "paragraph",
        content: [
          { type: "text", text: "Synced from Fathom", marks: [{ type: "em" }] },
        ],
      },
      {
        type: "paragraph",
        content: [
          {
            type: "text",
            text: `Complexity: ${item.complexity} | Priority: ${item.priority} | Est. Cost: $${item.estimatedCost.toFixed(2)}`,
            marks: [{ type: "strong" }],
          },
        ],
      },
      {
        type: "paragraph",
        content: [
          {
            type: "text",
            text: `Acceptance Criteria: ${item.acceptanceCriteria}`,
            marks: [{ type: "strong" }],
          },
        ],
      },
      {
        type: "paragraph",
        content: [
          { type: "text", text: `Slice ID: ${item.sliceId} | Category: ${item.category} | Estimated Tokens: ${item.estimatedTokens.toLocaleString()}` },
        ],
      },
    ],
  };
}

export function createJiraProvider(
  apiKey: string,
  projectKey: string,
  statusMapping?: Record<string, SyncStatus>,
): SyncProvider {
  const { host, project } = extractJiraConfig(projectKey);
  const statusMap = { ...DEFAULT_STATUS_MAP, ...statusMapping };

  function mapToFathomStatus(jiraStatus: string): SyncStatus {
    return statusMap[jiraStatus] ?? "backlog";
  }

  return {
    name: "jira",

    async createItem(item: SyncItem): Promise<SyncResult> {
      const result = await jiraRequest(host, apiKey, "POST", "/issue", {
        fields: {
          project: { key: project },
          summary: item.title,
          description: buildAdfDescription(item),
          issuetype: { name: "Task" },
          labels: ["fathom", `complexity-${item.complexity}`, `priority-${item.priority}`, ...item.labels],
        },
      });

      return {
        externalId: result.key as string,
        url: `${host}/browse/${result.key as string}`,
        provider: "jira",
      };
    },

    async updateStatus(externalId: string, status: SyncStatus): Promise<void> {
      // Get available transitions
      const transitionsData = await jiraRequest(
        host,
        apiKey,
        "GET",
        `/issue/${externalId}/transitions`,
      );

      const transitions = (transitionsData.transitions as Array<{
        id: string;
        name: string;
        to: { name: string };
      }>) ?? [];

      const targetName = REVERSE_STATUS_MAP[status] ?? "To Do";
      const transition = transitions.find(
        (t) => t.name === targetName || t.to.name === targetName,
      );

      if (!transition) {
        throw new Error(
          `Jira transition not found for status "${targetName}". Available: ${transitions.map((t) => t.name).join(", ")}`,
        );
      }

      await jiraRequest(host, apiKey, "POST", `/issue/${externalId}/transitions`, {
        transition: { id: transition.id },
      });
    },

    async getStatus(externalId: string): Promise<SyncStatus> {
      const issue = await jiraRequest(host, apiKey, "GET", `/issue/${externalId}?fields=status`);
      const fields = issue.fields as { status: { name: string } };
      return mapToFathomStatus(fields.status.name);
    },

    async listItems(): Promise<SyncedItem[]> {
      const jql = encodeURIComponent(`project = "${project}" AND labels = "fathom" ORDER BY created DESC`);
      const result = await jiraRequest(
        host,
        apiKey,
        "GET",
        `/search?jql=${jql}&fields=key,summary,status,updated`,
      );

      const issues = (result.issues as Array<{
        key: string;
        self: string;
        fields: {
          summary: string;
          status: { name: string };
          updated: string;
        };
      }>) ?? [];

      return issues.map((issue) => ({
        sliceId: issue.key,
        externalId: issue.key,
        url: `${host}/browse/${issue.key}`,
        status: mapToFathomStatus(issue.fields.status.name),
        lastSynced: new Date(issue.fields.updated).getTime(),
      }));
    },
  };
}
